import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Newspaper, X, ArrowRight, ExternalLink } from "lucide-react";
import { useNewsUnread } from "@/hooks/useNewsUnread";

const relTime = (iso) => {
    const t = Date.parse(iso || "");
    if (!t) return "";
    const m = Math.round((Date.now() - t) / 60000);
    if (m < 60) return `${Math.max(1, m)}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
};

/**
 * Floating news widget — small futuristic orb stacked right above the
 * Smart Bot in the bottom-right corner so the two share a column. A
 * pulsing teal count badge shows the number of stories newer than the
 * last one the user opened. Click → expands into a compact popover with
 * the freshest 5 headlines + "Open full feed →" link to /news.
 */
const FloatingNewsWidget = () => {
    const location = useLocation();
    const { items, unseenCount, markAllSeen } = useNewsUnread();
    const [open, setOpen] = useState(false);
    const popRef = useRef(null);
    const btnRef = useRef(null);

    // Close on outside click / Escape
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (popRef.current?.contains(e.target)) return;
            if (btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const handleOpen = () => {
        setOpen((v) => {
            if (!v) markAllSeen();
            return !v;
        });
    };

    // Hide on /news (it already shows the full feed) and on /login
    if (location.pathname.startsWith("/news") || location.pathname.startsWith("/login")) return null;

    return (
        <div className="fixed bottom-[5.5rem] right-6 z-[95]" data-testid="news-fab-wrap">
            <button
                ref={btnRef}
                onClick={handleOpen}
                data-testid="news-fab"
                aria-label={`Open news${unseenCount ? ` — ${unseenCount} new` : ""}`}
                className="group relative h-14 w-14 rounded-full overflow-hidden text-white flex items-center justify-center transition-all duration-300 hover:scale-[1.06] active:scale-95"
                style={{
                    // Brand-teal gradient pops on the light app chrome
                    // far better than the previous slate-dark fill.
                    background: "linear-gradient(135deg, #2EB4AC 0%, #1F7F79 55%, #0EA5A0 100%)",
                    boxShadow:
                        "0 0 0 2px rgba(255,255,255,0.9) inset, 0 0 0 4px rgba(46,180,172,0.25), 0 16px 36px -10px rgba(31,127,121,0.65), 0 0 28px -2px rgba(46,180,172,0.65)",
                    animation: "news-fab-bob 4.6s ease-in-out infinite",
                }}
            >
                {/* Bright ambient halo — matches the teal palette so the
                    orb feels luminous against the light app background. */}
                <span aria-hidden className="absolute -inset-3 rounded-full pointer-events-none"
                    style={{
                        background: "radial-gradient(60% 100% at 20% 50%, rgba(46,180,172,0.6) 0%, transparent 60%), radial-gradient(60% 100% at 80% 50%, rgba(14,165,160,0.5) 0%, transparent 60%)",
                        filter: "blur(14px)",
                        animation: "news-fab-glow 4.2s ease-in-out infinite",
                    }} />
                {/* Slow conic sheen rotates around the rim — gives the
                    orb a constant feeling of motion even when idle. */}
                <span aria-hidden className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                        background: "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(255,255,255,0.4) 60deg, transparent 140deg, transparent 360deg)",
                        animation: "news-fab-spin 6s linear infinite",
                        mixBlendMode: "overlay",
                        opacity: 0.9,
                    }} />
                {/* Hover sheen sweep — accents the constant spin */}
                <span aria-hidden className="absolute inset-0 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                        background: "conic-gradient(from 180deg at 50% 50%, transparent 0deg, rgba(255,255,255,0.45) 60deg, transparent 120deg)",
                        mixBlendMode: "overlay",
                    }} />
                <Newspaper
                    className="relative h-[22px] w-[22px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
                    style={{ animation: "news-fab-tilt 4.6s ease-in-out infinite" }}
                />
                {/* Count badge — shows the number of unread top stories.
                    Indigo so it contrasts with the teal orb rather than
                    blending into it; the surrounding ring is white to
                    pop against any background. */}
                {unseenCount > 0 && (
                    <>
                        <span
                            aria-hidden
                            className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full bg-[#4f46e5] text-white text-[11px] font-bold leading-none inline-flex items-center justify-center ring-2 ring-white shadow-[0_4px_10px_-2px_rgba(79,70,229,0.7)]"
                            data-testid="news-fab-count"
                        >
                            {unseenCount > 9 ? "9+" : unseenCount}
                        </span>
                        <span aria-hidden className="absolute -top-1 -right-1 min-w-[22px] h-[22px] rounded-full bg-[#4f46e5] animate-ping opacity-50" />
                    </>
                )}
                <style>{`
                    @keyframes news-fab-glow {
                        0%, 100% { opacity: 0.55; transform: scale(0.96); }
                        50% { opacity: 1; transform: scale(1.1); }
                    }
                    @keyframes news-fab-bob {
                        0%, 100% { transform: translateY(0); }
                        50%      { transform: translateY(-3px); }
                    }
                    @keyframes news-fab-spin {
                        0%   { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    @keyframes news-fab-tilt {
                        0%, 100% { transform: rotate(-4deg); }
                        50%      { transform: rotate(4deg); }
                    }
                `}</style>
            </button>

            {open && (
                <div
                    ref={popRef}
                    data-testid="news-fab-popover"
                    className="absolute bottom-16 right-0 w-80 rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-br from-[#2EB4AC] to-[#1F7F79] text-white">
                        <div>
                            <div className="font-heading font-bold text-sm inline-flex items-center gap-1.5">
                                <Newspaper className="h-4 w-4" /> Latest news
                            </div>
                            <div className="text-[10px] opacity-80 mt-0.5">India · refreshes every 5 min</div>
                        </div>
                        <button onClick={() => setOpen(false)} aria-label="Close" className="opacity-80 hover:opacity-100">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <ul className="max-h-80 overflow-y-auto scrollbar-thin">
                        {items.length === 0 ? (
                            <li className="px-4 py-6 text-xs text-slate-500 text-center">Loading…</li>
                        ) : items.map((it, idx) => (
                            <li key={it.link} className="border-b border-slate-50 last:border-0">
                                <a
                                    href={it.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`news-fab-item-${idx}`}
                                    className="block group px-4 py-3 hover:bg-slate-50 transition-colors"
                                >
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#25908A] font-semibold inline-flex items-center gap-1 truncate">
                                        <span className="truncate max-w-[10rem]">{it.source}</span>
                                        <span className="text-slate-400 font-medium normal-case tracking-normal">· {relTime(it.published)}</span>
                                    </div>
                                    <div className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 mt-0.5 group-hover:text-[#1F7F79]">
                                        {it.title}
                                    </div>
                                    <div className="text-[10px] text-slate-400 mt-1 inline-flex items-center gap-1">Read on Google News <ExternalLink className="h-2.5 w-2.5" /></div>
                                </a>
                            </li>
                        ))}
                    </ul>
                    <Link
                        to="/news"
                        onClick={() => setOpen(false)}
                        data-testid="news-fab-see-all"
                        className="flex items-center justify-center gap-1 px-4 py-3 text-xs font-semibold text-[#25908A] hover:bg-teal-50 border-t border-slate-100 transition-colors"
                    >
                        Open full feed <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
            )}
        </div>
    );
};

export default FloatingNewsWidget;
